import { configAPI } from '../api/client';
import { wsClient } from './websocket';
import { useDirectFileTransferStore, type DirectFileTransferMetadata } from '../store/directFileTransferStore';
import { saveReceivedDirectFile } from '../directFileTransfer/storage';
import { saveLocalDirectFileEvent } from '../messages/storage';
import type {
  WSDirectFileTransferAcceptedData,
  WSDirectFileTransferIceCandidateData,
  WSDirectFileTransferIncomingData,
  WSDirectFileTransferRejectedData
} from '@shared/types';

type SessionRuntime = {
  sessionId: string;
  direction: 'outgoing' | 'incoming';
  peerIdentityId: string;
  peerDisplayName: string;
  localIdentityId?: string;
  metadata: DirectFileTransferMetadata;
  serverUrl: string;
  peerConnection: RTCPeerConnection;
  dataChannel: RTCDataChannel | null;
  file?: File;
  pendingOffer?: RTCSessionDescriptionInit;
  pendingIceCandidates: RTCIceCandidateInit[];
  receivedChunks: Uint8Array[];
  receivedBytes: number;
  completed: boolean;
};

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' }
];
const CHUNK_SIZE = 16 * 1024;
const BUFFER_HIGH_WATERMARK = 512 * 1024;
const BUFFER_LOW_WATERMARK = 128 * 1024;

function isOfflineRejectReason(reason: string | undefined): boolean {
  return String(reason || '').trim().toLowerCase() === 'recipient is offline';
}

class DirectFileTransferManager {
  private sessions = new Map<string, SessionRuntime>();

  private async recordCompletedTransfer(session: SessionRuntime, receivedBlob?: Blob): Promise<void> {
    if (!session.localIdentityId) return;

    if (session.direction === 'incoming' && receivedBlob) {
      await saveReceivedDirectFile({
        sessionId: session.sessionId,
        localIdentityId: session.localIdentityId,
        fileName: session.metadata.fileName,
        mimeType: session.metadata.mimeType,
        size: session.metadata.size,
        receivedAt: Date.now(),
        blob: receivedBlob
      });
    }

    await saveLocalDirectFileEvent({
      sessionId: session.sessionId,
      localIdentityId: session.localIdentityId,
      peerIdentityId: session.peerIdentityId,
      fileName: session.metadata.fileName,
      size: session.metadata.size,
      mimeType: session.metadata.mimeType,
      direction: session.direction,
      localAvailable: session.direction === 'incoming' ? true : undefined
    });
  }

  private async getIceServers(serverUrl: string): Promise<RTCIceServer[]> {
    try {
      const response = await configAPI.getIceServers(serverUrl);
      if (response.status === 'ok' && response.result?.iceServers?.length) {
        return response.result.iceServers;
      }
    } catch {
      // ignore
    }
    return DEFAULT_ICE_SERVERS;
  }

  private async createPeerConnection(session: SessionRuntime): Promise<void> {
    const iceServers = await this.getIceServers(session.serverUrl);
    const pc = new RTCPeerConnection({ iceServers });
    session.peerConnection = pc;

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      wsClient.sendDirectFileTransferIceCandidate(session.sessionId, event.candidate.toJSON());
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      if (state === 'connecting' || state === 'connected') {
        useDirectFileTransferStore.getState().patchActiveSession({ status: 'connecting' });
      } else if (state === 'failed' || state === 'disconnected') {
        useDirectFileTransferStore.getState().patchActiveSession({
          status: 'failed',
          error: 'Direct file transfer connection failed.'
        });
      }
    };

    if (session.direction === 'incoming') {
      pc.ondatachannel = (event) => {
        session.dataChannel = event.channel;
        this.attachDataChannel(session);
      };
    }
  }

  private async flushPendingIceCandidates(session: SessionRuntime): Promise<void> {
    if (!session.peerConnection.remoteDescription || session.pendingIceCandidates.length === 0) {
      return;
    }

    const pending = [...session.pendingIceCandidates];
    session.pendingIceCandidates = [];
    for (const candidate of pending) {
      try {
        await session.peerConnection.addIceCandidate(candidate);
      } catch {
        // Ignore invalid/stale candidates after renegotiation or teardown.
      }
    }
  }

  private attachDataChannel(session: SessionRuntime) {
    const channel = session.dataChannel;
    if (!channel) return;
    channel.binaryType = 'arraybuffer';

    channel.onopen = () => {
      if (session.direction === 'outgoing') {
        useDirectFileTransferStore.getState().patchActiveSession({ status: 'transferring', progress: 0 });
        void this.sendFile(session);
      }
    };

    channel.onmessage = (event) => {
      if (!(event.data instanceof ArrayBuffer)) return;
      const chunk = new Uint8Array(event.data);
      session.receivedChunks.push(chunk);
      session.receivedBytes += chunk.byteLength;
      const progress = Math.min(100, Math.round((session.receivedBytes / session.metadata.size) * 100));
      useDirectFileTransferStore.getState().patchActiveSession({ status: 'transferring', progress });

      if (session.receivedBytes >= session.metadata.size && !session.completed) {
        session.completed = true;
        const blob = new Blob(session.receivedChunks.map((item) => Uint8Array.from(item)), {
          type: session.metadata.mimeType || 'application/octet-stream'
        });
        void this.recordCompletedTransfer(session, blob);
        useDirectFileTransferStore.getState().patchActiveSession({
          status: 'completed',
          progress: 100,
          receivedBlob: blob
        });
        try {
          channel.close();
        } catch {
          // ignore
        }
      }
    };

    channel.onclose = () => {
      if (session.direction === 'incoming' && !session.completed) {
        useDirectFileTransferStore.getState().patchActiveSession({
          status: 'failed',
          error: 'The transfer was interrupted before the whole file arrived.'
        });
      }
    };

    channel.onerror = () => {
      useDirectFileTransferStore.getState().patchActiveSession({
        status: 'failed',
        error: 'Direct file transfer channel error.'
      });
    };
  }

  private async sendFile(session: SessionRuntime) {
    const file = session.file;
    const channel = session.dataChannel;
    if (!file || !channel) return;

    let offset = 0;
    while (offset < file.size) {
      if (channel.readyState !== 'open') {
        useDirectFileTransferStore.getState().patchActiveSession({
          status: 'failed',
          error: 'The direct transfer channel was closed.'
        });
        return;
      }

      if (channel.bufferedAmount > BUFFER_HIGH_WATERMARK) {
        await new Promise<void>((resolve) => {
          const tick = () => {
            if (channel.readyState !== 'open' || channel.bufferedAmount <= BUFFER_LOW_WATERMARK) {
              resolve();
              return;
            }
            window.setTimeout(tick, 40);
          };
          tick();
        });
      }

      const chunk = await file.slice(offset, offset + CHUNK_SIZE).arrayBuffer();
      channel.send(chunk);
      offset += chunk.byteLength;
      const progress = Math.min(100, Math.round((offset / file.size) * 100));
      useDirectFileTransferStore.getState().patchActiveSession({ status: 'transferring', progress });
    }

    await this.recordCompletedTransfer(session);
    useDirectFileTransferStore.getState().patchActiveSession({ status: 'completed', progress: 100 });
    try {
      channel.close();
    } catch {
      // ignore
    }
  }

  private async sendOutgoingOffer(session: SessionRuntime): Promise<void> {
    await this.createPeerConnection(session);
    session.dataChannel = session.peerConnection.createDataChannel('direct-file-transfer', { ordered: true });
    this.attachDataChannel(session);

    const offer = await session.peerConnection.createOffer();
    await session.peerConnection.setLocalDescription(offer);
    useDirectFileTransferStore.getState().patchActiveSession({
      status: 'awaiting_accept',
      progress: 0,
      error: null,
      canRetry: false,
      offlineHint: false
    });
    wsClient.sendDirectFileTransferOffer(session.sessionId, session.peerIdentityId, session.metadata, offer);
  }

  async startOutgoingTransfer(params: {
    localServerUrl: string;
    localIdentityId: string;
    peerIdentityId: string;
    peerDisplayName: string;
    file: File;
  }): Promise<void> {
    const sessionId = `dft_${crypto.randomUUID()}`;
    const metadata: DirectFileTransferMetadata = {
      fileName: params.file.name || 'file',
      size: params.file.size,
      mimeType: params.file.type || 'application/octet-stream'
    };

    const runtime: SessionRuntime = {
      sessionId,
      direction: 'outgoing',
      peerIdentityId: params.peerIdentityId,
      peerDisplayName: params.peerDisplayName,
      localIdentityId: params.localIdentityId,
      metadata,
      serverUrl: params.localServerUrl,
      peerConnection: null as unknown as RTCPeerConnection,
      dataChannel: null,
      file: params.file,
      pendingIceCandidates: [],
      receivedChunks: [],
      receivedBytes: 0,
      completed: false
    };
    this.sessions.set(sessionId, runtime);

    useDirectFileTransferStore.getState().setActiveSession({
      sessionId,
      direction: 'outgoing',
      peerIdentityId: params.peerIdentityId,
      peerDisplayName: params.peerDisplayName,
      localIdentityId: params.localIdentityId,
      metadata,
      status: 'preparing',
      progress: 0,
      canRetry: false,
      offlineHint: false
    });

    await this.sendOutgoingOffer(runtime);
  }

  async retryOutgoingTransfer(sessionId: string): Promise<void> {
    const runtime = this.sessions.get(sessionId);
    if (!runtime || runtime.direction !== 'outgoing' || !runtime.file) return;

    try {
      if (runtime.peerConnection) {
        runtime.peerConnection.close();
      }
    } catch {
      // ignore stale peer connection
    }

    runtime.peerConnection = null as unknown as RTCPeerConnection;
    runtime.dataChannel = null;
    runtime.pendingIceCandidates = [];
    runtime.receivedChunks = [];
    runtime.receivedBytes = 0;
    runtime.completed = false;

    useDirectFileTransferStore.getState().patchActiveSession({
      status: 'preparing',
      progress: 0,
      error: null,
      canRetry: false,
      offlineHint: false
    });

    try {
      await this.sendOutgoingOffer(runtime);
    } catch (error) {
      useDirectFileTransferStore.getState().patchActiveSession({
        status: 'failed',
        error: error instanceof Error ? error.message : 'Failed to retry file transfer.',
        canRetry: true
      });
    }
  }

  startIncomingWait(params: {
    localIdentityId: string;
    peerIdentityId: string;
    peerDisplayName: string;
    serverUrl: string;
  }) {
    useDirectFileTransferStore.getState().setWaitingRequest({
      localIdentityId: params.localIdentityId,
      peerIdentityId: params.peerIdentityId,
      peerDisplayName: params.peerDisplayName,
      serverUrl: params.serverUrl,
      startedAt: Date.now()
    });
    useDirectFileTransferStore.getState().setIncomingRequest(null);
    wsClient.connect();
  }

  cancelIncomingWait() {
    useDirectFileTransferStore.getState().setWaitingRequest(null);
  }

  handleIncomingOffer(data: WSDirectFileTransferIncomingData) {
    const runtime: SessionRuntime = {
      sessionId: data.sessionId,
      direction: 'incoming',
      peerIdentityId: data.fromIdentityId,
      peerDisplayName: data.fromIdentityName || data.fromIdentityId,
      metadata: data.metadata,
      serverUrl: '',
      peerConnection: null as unknown as RTCPeerConnection,
      dataChannel: null,
      pendingOffer: data.offer as RTCSessionDescriptionInit,
      pendingIceCandidates: [],
      receivedChunks: [],
      receivedBytes: 0,
      completed: false
    };
    this.sessions.set(data.sessionId, runtime);

    const waitingRequest = useDirectFileTransferStore.getState().waitingRequest;
    if (waitingRequest?.peerIdentityId === data.fromIdentityId) {
      useDirectFileTransferStore.getState().setIncomingRequest(null);
      void this.acceptIncomingTransfer(
        data.sessionId,
        waitingRequest.serverUrl,
        waitingRequest.localIdentityId
      );
      return;
    }

    useDirectFileTransferStore.getState().setIncomingRequest({
      sessionId: data.sessionId,
      fromIdentityId: data.fromIdentityId,
      fromIdentityName: data.fromIdentityName,
      metadata: data.metadata
    });
  }

  async acceptIncomingTransfer(sessionId: string, serverUrl: string, localIdentityId: string): Promise<void> {
    const runtime = this.sessions.get(sessionId);
    if (!runtime || !runtime.pendingOffer) return;
    runtime.serverUrl = serverUrl;
    runtime.localIdentityId = localIdentityId;
    await this.createPeerConnection(runtime);
    await runtime.peerConnection.setRemoteDescription(runtime.pendingOffer);
    await this.flushPendingIceCandidates(runtime);

    useDirectFileTransferStore.getState().setIncomingRequest(null);
    useDirectFileTransferStore.getState().setWaitingRequest(null);
    useDirectFileTransferStore.getState().setActiveSession({
      sessionId,
      direction: 'incoming',
      peerIdentityId: runtime.peerIdentityId,
      peerDisplayName: runtime.peerDisplayName,
      localIdentityId,
      metadata: runtime.metadata,
      status: 'connecting',
      progress: 0
    });

    const answer = await runtime.peerConnection.createAnswer();
    await runtime.peerConnection.setLocalDescription(answer);
    wsClient.sendDirectFileTransferAccept(sessionId, answer);
  }

  rejectIncomingTransfer(sessionId: string, reason = 'rejected_by_user') {
    this.sessions.delete(sessionId);
    useDirectFileTransferStore.getState().setIncomingRequest(null);
    wsClient.sendDirectFileTransferReject(sessionId, reason);
  }

  async handleAccepted(data: WSDirectFileTransferAcceptedData) {
    const runtime = this.sessions.get(data.sessionId);
    if (!runtime) return;
    await runtime.peerConnection.setRemoteDescription(data.answer as RTCSessionDescriptionInit);
    await this.flushPendingIceCandidates(runtime);
    useDirectFileTransferStore.getState().patchActiveSession({ status: 'connecting' });
  }

  handleRejected(data: WSDirectFileTransferRejectedData) {
    const runtime = this.sessions.get(data.sessionId);
    if (runtime?.peerConnection) {
      runtime.peerConnection.close();
    }
    const offlineReject = isOfflineRejectReason(data.reason);
    const canRetry = offlineReject && runtime?.direction === 'outgoing' && !!runtime.file;
    if (!canRetry) {
      this.sessions.delete(data.sessionId);
    }
    useDirectFileTransferStore.getState().setWaitingRequest(null);
    useDirectFileTransferStore.getState().patchActiveSession({
      status: 'rejected',
      error: data.reason,
      canRetry,
      offlineHint: offlineReject
    });
  }

  dismissSession(sessionId: string) {
    const runtime = this.sessions.get(sessionId);
    if (runtime?.peerConnection) {
      runtime.peerConnection.close();
    }
    this.sessions.delete(sessionId);
    useDirectFileTransferStore.getState().clearActiveSession();
  }

  async handleIceCandidate(data: WSDirectFileTransferIceCandidateData) {
    const runtime = this.sessions.get(data.sessionId);
    if (!runtime) return;
    if (!runtime.peerConnection.remoteDescription) {
      runtime.pendingIceCandidates.push(data.candidate as RTCIceCandidateInit);
      return;
    }
    try {
      await runtime.peerConnection.addIceCandidate(data.candidate as RTCIceCandidateInit);
    } catch {
      // ignore out-of-order candidates in this first pass
    }
  }

  endSession(sessionId: string, reason = 'cancelled') {
    const runtime = this.sessions.get(sessionId);
    if (runtime?.peerConnection) {
      runtime.peerConnection.close();
    }
    this.sessions.delete(sessionId);
    const activeSession = useDirectFileTransferStore.getState().activeSession;
    if (activeSession?.sessionId === sessionId && activeSession.status === 'completed') {
      useDirectFileTransferStore.getState().clearActiveSession();
      return;
    }
    if (reason === 'cancelled') {
      useDirectFileTransferStore.getState().clearActiveSession();
      useDirectFileTransferStore.getState().setIncomingRequest(null);
      useDirectFileTransferStore.getState().setWaitingRequest(null);
      wsClient.sendDirectFileTransferCancel(sessionId, reason);
      return;
    }
    useDirectFileTransferStore.getState().patchActiveSession({
      status: 'failed',
      error: reason
    });
    wsClient.sendDirectFileTransferCancel(sessionId, reason);
  }

  handleEnded(sessionId: string, reason: string) {
    const runtime = this.sessions.get(sessionId);
    if (runtime?.peerConnection) {
      runtime.peerConnection.close();
    }
    this.sessions.delete(sessionId);
    useDirectFileTransferStore.getState().setIncomingRequest(null);
    useDirectFileTransferStore.getState().setWaitingRequest(null);
    useDirectFileTransferStore.getState().patchActiveSession({
      status: reason === 'cancelled' ? 'cancelled' : 'failed',
      error: reason === 'cancelled' ? null : reason
    });
  }
}

export const directFileTransferManager = new DirectFileTransferManager();
